<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import assignmentPoliciesAPI from 'dashboard/api/assignmentPolicies';
import { useI18n } from 'vue-i18n';
import TeamForm from '../TeamForm.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import Spinner from 'shared/components/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isEnterprise } = useConfig();

const assignmentPolicy = ref(null);
const isLoadingPolicy = ref(false);
const availablePolicies = ref([]);
const isLoadingPolicies = ref(false);
const showPolicyDropdown = ref(false);
const isLinkingPolicy = ref(false);
const isDeletingPolicy = ref(false);
const showDeleteConfirmModal = ref(false);

const teamId = computed(() => route.params.teamId);

const teamData = computed(() => store.getters['teams/getTeam'](teamId.value));
const uiFlags = computed(() => store.getters['teams/getUIFlags']);
const showTeamForm = computed(() => teamData.value?.id && !uiFlags.value.isFetching);

const isFeatureEnabled = feature => {
  const accountId = Number(route.params.accountId);
  return store.getters['accounts/isFeatureEnabledonAccount'](accountId, feature);
};

const hasAdvancedAssignment = computed(() => isFeatureEnabled('advanced_assignment'));
const hasAssignmentV2 = computed(() => isFeatureEnabled('assignment_v2'));
const showAdvancedAssignmentUI = computed(() => hasAdvancedAssignment.value && hasAssignmentV2.value);

const assignmentOrderLabel = computed(() => {
  if (!assignmentPolicy.value) return '';
  const priority = assignmentPolicy.value.conversation_priority;
  if (priority === 'earliest_created') return t('INBOX_MGMT.ASSIGNMENT.PRIORITY.EARLIEST_CREATED');
  if (priority === 'longest_waiting') return t('INBOX_MGMT.ASSIGNMENT.PRIORITY.LONGEST_WAITING');
  return priority;
});

const assignmentMethodLabel = computed(() => {
  if (!assignmentPolicy.value) return '';
  const order = assignmentPolicy.value.assignment_order;
  if (order === 'round_robin') return t('INBOX_MGMT.ASSIGNMENT.METHOD.ROUND_ROBIN');
  if (order === 'balanced') return t('INBOX_MGMT.ASSIGNMENT.METHOD.BALANCED');
  return order;
});

const fetchAssignmentPolicy = async () => {
  if (!teamId.value) return;
  isLoadingPolicy.value = true;
  try {
    const response = await assignmentPoliciesAPI.getTeamPolicy(teamId.value);
    assignmentPolicy.value = response.data;
  } catch {
    assignmentPolicy.value = null;
  } finally {
    isLoadingPolicy.value = false;
  }
};

const fetchAvailablePolicies = async () => {
  isLoadingPolicies.value = true;
  try {
    const response = await assignmentPoliciesAPI.get();
    availablePolicies.value = response.data;
  } catch {
    availablePolicies.value = [];
  } finally {
    isLoadingPolicies.value = false;
  }
};

const linkPolicyToTeam = async policy => {
  isLinkingPolicy.value = true;
  try {
    await assignmentPoliciesAPI.setTeamPolicy(teamId.value, policy.id);
    assignmentPolicy.value = policy;
    showPolicyDropdown.value = false;
    useAlert(t('INBOX_MGMT.ASSIGNMENT.LINK_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.ASSIGNMENT.LINK_ERROR'));
  } finally {
    isLinkingPolicy.value = false;
  }
};

const policyMenuItems = computed(() => {
  const items = availablePolicies.value.map(policy => ({
    action: 'select_policy',
    value: policy.id,
    label: policy.name,
    icon: 'i-lucide-zap',
    policy,
  }));
  items.push({
    action: 'view_all',
    value: 'view_all',
    label: t('INBOX_MGMT.ASSIGNMENT.VIEW_ALL_POLICIES'),
    icon: 'i-lucide-arrow-right',
  });
  return items;
});

const handlePolicyMenuAction = ({ action, policy }) => {
  if (action === 'select_policy' && policy) {
    linkPolicyToTeam(policy);
  } else if (action === 'view_all') {
    navigateToAssignmentPolicies();
  }
  showPolicyDropdown.value = false;
};

const togglePolicyDropdown = () => {
  if (!showPolicyDropdown.value && availablePolicies.value.length === 0) {
    fetchAvailablePolicies();
  }
  showPolicyDropdown.value = !showPolicyDropdown.value;
};

const closePolicyDropdown = () => {
  showPolicyDropdown.value = false;
};

const navigateToAssignmentPolicies = () => {
  router.push({
    name: 'agent_assignment_policy_index',
    params: { accountId: route.params.accountId },
  });
};

const navigateToAssignmentPolicyEdit = () => {
  if (!assignmentPolicy.value?.id) return;
  router.push({
    name: 'agent_assignment_policy_edit',
    params: { accountId: route.params.accountId, id: assignmentPolicy.value.id },
  });
};

const navigateToCreatePolicy = () => {
  router.push({
    name: 'agent_assignment_policy_create',
    params: { accountId: route.params.accountId },
  });
};

const confirmDeletePolicy = () => {
  showDeleteConfirmModal.value = true;
};

const cancelDeletePolicy = () => {
  showDeleteConfirmModal.value = false;
};

const deleteAssignmentPolicy = async () => {
  if (isDeletingPolicy.value) return;
  isDeletingPolicy.value = true;
  try {
    await assignmentPoliciesAPI.removeTeamPolicy(teamId.value);
    assignmentPolicy.value = null;
    showDeleteConfirmModal.value = false;
    useAlert(t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_ERROR'));
  } finally {
    isDeletingPolicy.value = false;
  }
};

const updateTeam = async data => {
  try {
    await store.dispatch('teams/update', { id: teamId.value, ...data });
    router.replace({
      name: 'settings_teams_edit_members',
      params: { page: 'edit', teamId: teamId.value },
    });
  } catch {
    useAlert(t('TEAMS_SETTINGS.TEAM_FORM.ERROR_MESSAGE'));
  }
};

onMounted(() => {
  if (hasAssignmentV2.value) {
    fetchAssignmentPolicy();
    fetchAvailablePolicies();
  }
});

watch(teamId, () => {
  if (hasAssignmentV2.value) {
    fetchAssignmentPolicy();
  }
});
</script>

<template>
  <div class="h-full w-full p-8 col-span-6 overflow-y-auto">
    <PageHeader
      :header-title="$t('TEAMS_SETTINGS.EDIT_FLOW.CREATE.TITLE')"
      :header-content="$t('TEAMS_SETTINGS.EDIT_FLOW.CREATE.DESC')"
    />
    <div class="flex flex-wrap">
      <TeamForm
        v-if="showTeamForm"
        :on-submit="updateTeam"
        :submit-in-progress="false"
        :submit-button-text="$t('TEAMS_SETTINGS.EDIT_FLOW.CREATE.BUTTON_TEXT')"
        :form-data="teamData"
      />
      <Spinner v-else />
    </div>

    <!-- Assignment Policy Section -->
    <div v-if="hasAssignmentV2 && showTeamForm" class="mt-8 w-full">
      <div class="rounded-xl border border-n-weak p-6">
        <h3 class="text-heading-2 text-n-slate-12 mb-1">
          Política de Atribuição do Time
        </h3>
        <p class="text-body-main text-n-slate-11 mb-4">
          Defina regras de distribuição específicas para este time. Se configurada, terá prioridade sobre a política da inbox.
        </p>

        <!-- Policy attached -->
        <div v-if="assignmentPolicy" class="flex items-start gap-4">
          <div class="flex-shrink-0 size-10 rounded-xl bg-n-slate-3 flex items-center justify-center">
            <span class="i-lucide-zap text-xl text-n-slate-11" />
          </div>
          <div class="flex-grow">
            <div class="flex items-start justify-between gap-4 mb-4">
              <div class="flex flex-col items-start">
                <span class="text-heading-3 text-n-slate-12 mb-1">
                  {{ assignmentPolicy.name }}
                </span>
                <p class="text-body-main text-n-slate-11">
                  {{ $t('INBOX_MGMT.ASSIGNMENT.POLICY_LABEL') }}
                </p>
              </div>
              <NextButton
                icon="i-lucide-trash-2"
                ghost
                ruby
                sm
                @click="confirmDeletePolicy"
              />
            </div>

            <ul class="space-y-2 mb-4">
              <li class="flex items-center gap-2">
                <span class="w-1.5 h-1.5 rounded-full bg-n-slate-11 flex-shrink-0" />
                <span class="text-body-main text-n-slate-12">
                  {{ assignmentOrderLabel }}
                </span>
              </li>
              <li class="flex items-center gap-2">
                <span class="w-1.5 h-1.5 rounded-full bg-n-slate-11 flex-shrink-0" />
                <span class="text-body-main text-n-slate-12">
                  {{ assignmentMethodLabel }}
                </span>
              </li>
            </ul>

            <div class="w-full h-px my-4 bg-n-weak" />

            <NextButton
              label="Personalizar Política"
              icon="i-lucide-arrow-right"
              trailing-icon
              link
              class="mb-2"
              @click="navigateToAssignmentPolicyEdit"
            />
          </div>
        </div>

        <!-- No policy attached -->
        <div v-else-if="!isLoadingPolicy">
          <div class="flex items-start gap-4 mb-4">
            <div class="flex-shrink-0 size-10 rounded-xl bg-n-slate-3 flex items-center justify-center">
              <Icon icon="i-lucide-zap" class="text-xl text-n-slate-11" />
            </div>
            <div class="flex-grow">
              <h4 class="text-heading-3 text-n-slate-12 mb-0.5">
                Nenhuma política vinculada
              </h4>
              <p class="text-body-main text-n-slate-11">
                As conversas deste time usarão a política da inbox como fallback.
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div
              v-if="!isLoadingPolicies && availablePolicies.length > 0"
              v-on-click-outside="closePolicyDropdown"
              class="relative"
            >
              <NextButton
                icon="i-lucide-link"
                sm
                @click="togglePolicyDropdown"
              >
                Vincular Política Existente
                <Icon
                  icon="i-lucide-chevron-down"
                  class="transition-transform flex-shrink-0"
                  :class="{ 'rotate-180': showPolicyDropdown }"
                />
              </NextButton>

              <DropdownMenu
                v-if="showPolicyDropdown"
                class="top-full ltr:left-0 rtl:right-0 mt-2 max-w-64 max-h-72 overflow-y-auto"
                :menu-items="policyMenuItems"
                :is-searching="isLoadingPolicies"
                @action="handlePolicyMenuAction"
              />
            </div>

            <NextButton
              icon="i-lucide-plus"
              label="Criar Nova Política"
              slate
              faded
              sm
              @click="navigateToCreatePolicy"
            />
          </div>

          <div class="px-4 py-3 border-t border-n-weak bg-n-slate-2 rounded-b-xl mt-4 -mx-6 -mb-6">
            <div class="flex items-start gap-3">
              <Icon icon="i-lucide-info" class="mt-0.5 text-n-slate-11" />
              <p class="text-body-main text-n-slate-11">
                Quando uma política é vinculada ao time, ela tem prioridade sobre a política configurada na inbox.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirm Modal -->
    <woot-modal
      v-if="showDeleteConfirmModal"
      :show="showDeleteConfirmModal"
      :on-close="cancelDeletePolicy"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium text-n-slate-12 mb-4">
          {{ $t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_CONFIRM_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-6 ml-13">
          Ao remover a política do time, as conversas passarão a usar a política da inbox.
        </p>
        <div class="flex justify-end gap-2">
          <NextButton
            color="slate"
            :label="$t('INBOX_MGMT.ASSIGNMENT_POLICY.CANCEL')"
            @click="cancelDeletePolicy"
          />
          <NextButton
            color="ruby"
            :label="$t('INBOX_MGMT.ASSIGNMENT_POLICY.CONFIRM_DELETE')"
            :is-loading="isDeletingPolicy"
            @click="deleteAssignmentPolicy"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>
